import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { canPerformAction } from 'actions/userActions';
import { useAppDispatch } from 'hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Condition, ForwardRule, Node } from 'reducers/nodesReducer';
import { availableOperations } from 'reducers/userReducer';
import { deleteCondition, forceGetConditions, insertCondition, updateCondition } from '../../../actions/conditionsActions';
import useToaster from '../../../actions/useToaster';
import { getConditions, getUser, isTouchedNode, isTouchedRule } from '../../../reducers/selectors';
import ConditionPopup from './ConditionPopup';
import ConditionRow from './ConditionRow';

type TConditionsTable = {
  rules: ForwardRule[];
  selectedNode: Node | null;
  selectedRule: ForwardRule | null;
};

function ConditionsTable({ rules, selectedNode, selectedRule }: TConditionsTable): React.ReactElement {
  const dispatch = useAppDispatch();
  const toaster = useToaster();
  const user = useSelector(getUser);
  const conditions: Condition[] = useSelector(getConditions);
  const isLastTouchedNode = useSelector(isTouchedNode);
  const isLastTouchedRule = useSelector(isTouchedRule);

  const [showConditions, setShowConditions] = useState<boolean>(false);
  const [editingCondition, setEditingCondition] = useState<Condition | null>(null);
  const [deletingCondition, setDeletingCondition] = useState<Condition | null>(null);
  const [showNewConditionPopup, setShowNewConditionPopup] = useState<boolean>(false);
  const [selectedNodeConditions, setSelectedNodeConditions] = useState<Condition[]>([]);
  const [selectedRuleConditions, setSelectedRuleConditions] = useState<Condition[]>([]);

  const authorized = useMemo(() => canPerformAction(user, availableOperations.CAN_MANAGE_NODES), [user]);

  useEffect(() => {
    if (selectedNode) {
      setSelectedNodeConditions(conditions.filter(condition => condition.sourceNodeAeTitle === selectedNode.aeTitle));
    }

    if (selectedRule) {
      setSelectedRuleConditions(
        conditions.filter(
          condition =>
            condition.sourceNodeAeTitle === selectedRule!.from && condition.destinationNodeAeTitle === selectedRule!.to
        )
      );
    }
  }, [selectedNode, selectedRule, conditions]);

  return (
    <div style={{ marginTop: '5em' }}>
      <strong>Conditions</strong>
      <Form.Check
        custom
        id="showConditions"
        type="checkbox"
        style={{ float: 'right', margin: '0' }}
        checked={showConditions}
        onChange={() => setShowConditions(!showConditions)}
        label="Show All"
      />
      <table className="queue-table">
        <thead>
          <tr>
            <th style={{ width: '8%' }}>From</th>
            <th style={{ width: '2%' }} />
            <th style={{ width: '8%' }}>To</th>
            <th style={{ width: '14%' }}>Property</th>
            <th style={{ width: '6%' }}>Criteria</th>
            <th style={{ width: '20%' }}>Reference Value</th>
            <th style={{ width: '6%' }} />
            <th style={{ width: '5%' }} />
          </tr>
        </thead>
        <tbody>
          {showConditions && conditions.length === 0 && (
            <tr>
              <td style={{ textAlign: 'center' }} colSpan={8}>
                No condition configured
              </td>
            </tr>
          )}

          {!showConditions && selectedNode == null && selectedRule == null && (
            <tr>
              <td style={{ textAlign: 'center' }} colSpan={8}>
                Select a node or a rule
              </td>
            </tr>
          )}

          {!showConditions && isLastTouchedNode && selectedNode && selectedNodeConditions.length === 0 && (
            <tr>
              <td style={{ textAlign: 'center' }} colSpan={8}>
                Node <strong>{selectedNode.aeTitle}</strong> has no conditions in any rule
              </td>
            </tr>
          )}
          {!showConditions && isLastTouchedRule && selectedRule && selectedRuleConditions.length === 0 && (
            <tr>
              <td style={{ textAlign: 'center' }} colSpan={8}>
                Rule{' '}
                <strong>
                  {selectedRule.from} → {selectedRule.to}
                </strong>{' '}
                has no conditions
              </td>
            </tr>
          )}

          {!showConditions &&
            isLastTouchedNode &&
            selectedNode &&
            selectedNodeConditions.length > 0 &&
            selectedNodeConditions.map(condition => (
              <ConditionRow
                key={condition.pk}
                condition={condition}
                authorized={authorized}
                onEdit={() => setEditingCondition(condition)}
                onDelete={() => setDeletingCondition(condition)}
              />
            ))}

          {!showConditions &&
            isLastTouchedRule &&
            selectedRule &&
            selectedRuleConditions.length > 0 &&
            selectedRuleConditions.map(condition => (
              <ConditionRow
                key={condition.pk}
                condition={condition}
                authorized={authorized}
                onEdit={() => setEditingCondition(condition)}
                onDelete={() => setDeletingCondition(condition)}
              />
            ))}

          {showConditions &&
            conditions.length > 0 &&
            conditions.map(condition => (
              <ConditionRow
                key={condition.pk}
                condition={condition}
                authorized={authorized}
                onEdit={() => setEditingCondition(condition)}
                onDelete={() => setDeletingCondition(condition)}
              />
            ))}
        </tbody>
      </table>
      <Button
        size="sm"
        variant="primary"
        style={{
          float: 'right',
          marginLeft: '2em',
        }}
        disabled={!authorized}
        onClick={() => setShowNewConditionPopup(true)}
      >
        Add new condition
        <FontAwesomeIcon icon={faPlusCircle} style={{ marginLeft: '5px' }} />
      </Button>

      {showNewConditionPopup && (
        <NewConditionPopup
          onClose={() => setShowNewConditionPopup(false)}
          onSave={condition => {
            insertCondition(condition)
              .then(() => {
                toaster.success('New condition added');
                setShowNewConditionPopup(false);
                dispatch(forceGetConditions());
              })
              .catch(error => {
                console.error(error);
                toaster.error('Error creating condition');
              });
          }}
          rules={rules}
        />
      )}

      {editingCondition && (
        <EditConditionPopup
          rules={rules}
          condition={editingCondition}
          onClose={() => setEditingCondition(null)}
          onSave={condition => {
            updateCondition(condition)
              .then(() => {
                toaster.success('Updated condition');
                setEditingCondition(null);
                dispatch(forceGetConditions());
              })
              .catch(error => {
                console.error(error);
                toaster.error('Error updating condition');
              });
          }}
        />
      )}

      {deletingCondition && (
        <DeleteConditionPopup
          condition={deletingCondition}
          onClose={() => setDeletingCondition(null)}
          onConfirm={() => {
            deleteCondition(deletingCondition.pk)
              .then(() => {
                toaster.success('Condition deleted');
                setDeletingCondition(null);
                dispatch(forceGetConditions());
              })
              .catch(error => {
                console.error(error);
                toaster.error('Error deleting condition');
              });
          }}
        />
      )}
    </div>
  );
}

const NewConditionPopup = ({
  rules,
  onClose,
  onSave,
}: {
  rules: ForwardRule[];
  onClose: () => void;
  onSave: (_: Condition) => void;
}) => (
  <Modal show size="lg">
    <Modal.Header>
      <Modal.Title>New condition</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <ConditionPopup isEditing={false} condition={null} rules={rules} onClose={onClose} onSave={onSave} />
    </Modal.Body>
  </Modal>
);

const EditConditionPopup = ({
  rules,
  condition,
  onClose,
  onSave,
}: {
  rules: ForwardRule[];
  condition: Condition;
  onClose: () => void;
  onSave: (_: Condition) => void;
}) => (
  <Modal show size="lg">
    <Modal.Header>
      <Modal.Title>
        Editing condition from {condition.sourceNodeAeTitle} to {condition.destinationNodeAeTitle}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <ConditionPopup isEditing rules={rules} condition={condition} onClose={onClose} onSave={onSave}/>
    </Modal.Body>
  </Modal>
);

const DeleteConditionPopup = ({
  condition,
  onClose,
  onConfirm,
}: {
  condition: Condition;
  onClose: () => void;
  onConfirm: () => void;
}) => (
  <Modal show>
    <Modal.Header>
      <Modal.Title>Deleting condition</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>
        Deleting condition from {condition.sourceNodeAeTitle} to {condition.destinationNodeAeTitle}
      </p>
      <p>Are you sure?</p>
      <div style={{ textAlign: 'right' }}>
        <Button onClick={onClose} variant="outline-secondary" style={{ marginRight: '5px' }}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onConfirm}>
          Confirm delete
        </Button>
      </div>
    </Modal.Body>
  </Modal>
);

export default ConditionsTable;
